import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { noop } from "lodash";
import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import useTableItemDetails from "@/components/DetailsSection/hooks/useTableItemDetails";
import Dialog from "@/components/Dialog/Dialog";
import { DialogWidth } from "@/components/Dialog/types/enums";
import { useAuth } from "@/context/Auth/AuthProvider";
import {
  ANALYSIS_PROVIDER_NAMES,
  CONVEY_INTEGRATION_FIELD_NAMES
} from "@/Pages/Integrations/types/enums";
import {
  ConveyWebErrors,
  ConveyWebObjectState
} from "@/Pages/Integrations/types/interfaces";
import { useMediaQueries } from "@/shared";
import DialogActions from "@/shared/components/buttons/DialogActions";
import useResponse from "@/shared/hooks/useResponse";
import { useIntegrationsDialogStore } from "@/stores/useIntegrationsDialogStore";
import { useAnalysisProvidersTableStore } from "@/stores/useTableStore";

import ConveyIntegrationContent from "../ConveyIntegrationContent";

interface AddConveyIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddConveyIntegration = ({
  isOpen,
  onClose
}: AddConveyIntegrationProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();
  const { getTableItemDetails } = useTableItemDetails();
  const { postData } = useAxiosPrivate();
  const { user } = useAuth();
  const { handleCommonError } = useResponse();

  const analysisProvidersParams = useAnalysisProvidersTableStore(
    state => state.params
  );

  const goBackToTypeSelect = useIntegrationsDialogStore(
    state => state.goBackToTypeSelect
  );

  const initialConveyWebObject: ConveyWebObjectState = {
    [CONVEY_INTEGRATION_FIELD_NAMES.Username]: "",
    [CONVEY_INTEGRATION_FIELD_NAMES.Password]: "",
    [CONVEY_INTEGRATION_FIELD_NAMES.SelectedCompany]:
      user?.selectedCompany ?? null,
    [CONVEY_INTEGRATION_FIELD_NAMES.Name]: "",
    [CONVEY_INTEGRATION_FIELD_NAMES.Enabled]: true,
    [CONVEY_INTEGRATION_FIELD_NAMES.IncludeSubCompanies]: false
  };

  const initialErrors: ConveyWebErrors = {
    [CONVEY_INTEGRATION_FIELD_NAMES.Username]: "",
    [CONVEY_INTEGRATION_FIELD_NAMES.Password]: "",
    [CONVEY_INTEGRATION_FIELD_NAMES.Name]: ""
  };

  const [conveyWebObject, setConveyWebObject] = useState<ConveyWebObjectState>(
    initialConveyWebObject
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ConveyWebErrors>(initialErrors);

  const resetAllValues = () => {
    setConveyWebObject(initialConveyWebObject);
    setErrors(initialErrors);
  };

  useEffect(() => {
    resetAllValues();
  }, [user?.selectedCompany]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const { selectedCompany, ...cleanedIntegrationsObject } = conveyWebObject;

      const payloadObject = {
        ...cleanedIntegrationsObject,
        companyId: conveyWebObject.selectedCompany?.id,
        provider: ANALYSIS_PROVIDER_NAMES.Convey
      };

      const { data } = await postData(
        ApiResources.IntegrationsAnalysisProviders,
        payloadObject
      );

      const successMessage = t("Alert##add integration success");

      const searchParams = analysisProvidersParams.contains;

      getTableItemDetails({
        itemId: data.id,
        successMessage,
        leaveTableDetailsNotUpdated: true,
        valueInSearchField: searchParams ?? undefined
      });

      onClose();
      resetAllValues();
    } catch (error) {
      handleCommonError(error, t);
    }
    setIsLoading(false);
  };

  const requiredFieldsFilled = Object.entries(conveyWebObject).every(
    ([_, value]) =>
      value !== null && value !== undefined && String(value).trim() !== ""
  );

  const hasErrors = Object.values(errors).some(error => error !== "");

  const isReadyToConfirm = !hasErrors && requiredFieldsFilled;

  return (
    <Dialog
      data-testid="add-convey-web-integration-dialog"
      title={t("Dialog##convey integration##add##title")}
      description={t("Dialog##convey integration##add##description")}
      extendToMaxWidth={toMd}
      isTitleSeparator
      isActionsSeparator
      open={isOpen}
      close={isLoading ? noop : onClose}
      submit={handleSubmit}
      width={toMd ? undefined : DialogWidth.Dialog920px}
      slotProps={{ transition: { onExited: resetAllValues } }}
      actions={
        <DialogActions
          onClose={goBackToTypeSelect}
          disabledClose={isLoading}
          isLoading={isLoading}
          isreadyToConfirm={isReadyToConfirm}
          confirmationButtonText={t("Button##add")}
          confirmationButtonTestId="add-convey-web-integration-button"
          cancelButtonText={t("Button##back")}
          cancelButtonTestId="back-convey-web-integration-button"
        />
      }
    >
      <ConveyIntegrationContent
        isLoading={isLoading}
        conveyWebObject={conveyWebObject}
        errors={errors}
        setErrors={setErrors}
        setConveyWebObject={setConveyWebObject}
      />
    </Dialog>
  );
};

export default AddConveyIntegration;
